/** Correcting what was created — **F-017 and gates 14 and 16**.
 *
 * Creation had hooks and a screen; correction had neither, so a typo in a company's name, a site on
 * the wrong grid region, a subsidiary recorded as always owned that was bought in August, or an
 * Auditor invited for the wrong year could only be left as it was.
 *
 * **What a correction invalidates is what reads the thing corrected, and it is not the same list
 * for each.** A label (a name, a postcode) reaches reports as text, so the lists and the reports
 * and the issued-artifact re-derivations go stale. A recorded acquisition reaches the group, the
 * report's partial-member notice and the structural-change notice, and **does not** reach the
 * determinations already on file - those are persisted with their own reasoning and only the next
 * "determine" reads the edge. So `["obligations"]` is deliberately not invalidated by an ownership
 * change: refetching it would show last year's answer unchanged and imply the edit had done
 * nothing, when the screen's own sentence says a re-determination is the person's to run.
 *
 * **Every refusal is the server's sentence and its code.** `fact_in_use` (409) is the one a screen
 * branches on: it means *this is not editable now, and here is what depends on it*, which is a
 * different remedy from a 422's *fix what you typed*.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { provisioningRefusal } from "./provisioning";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type EntityDetail = components["schemas"]["EntityDetailOut"];
export type EntityAmend = components["schemas"]["EntityAmendIn"];
export type EntityAmended = components["schemas"]["EntityAmendedOut"];
export type FacilityDetail = components["schemas"]["FacilityDetailOut"];
export type FacilityAmend = components["schemas"]["FacilityAmendIn"];
export type FacilityAmended = components["schemas"]["FacilityAmendedOut"];
export type Ownership = components["schemas"]["OwnershipOut"];
export type OwnershipIn = components["schemas"]["OwnershipIn"];
export type OwnershipAmend = components["schemas"]["OwnershipAmendIn"];
export type OwnershipRecorded = components["schemas"]["OwnershipRecordedOut"];
export type OwnershipAmended = components["schemas"]["OwnershipAmendedOut"];
export type MemberPeriodChange = components["schemas"]["MemberPeriodIn"];
export type MemberPeriod = components["schemas"]["MemberPeriodOut"];

/** What a label correction makes stale: every list that prints the name, and every report and
 * issued filing's re-derivation, which carries it. */
function invalidateLabels(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["entities"] });
  void queryClient.invalidateQueries({ queryKey: ["entity"] });
  void queryClient.invalidateQueries({ queryKey: ["facilities"] });
  void queryClient.invalidateQueries({ queryKey: ["facility"] });
  void queryClient.invalidateQueries({ queryKey: ["report"] });
  void queryClient.invalidateQueries({ queryKey: ["artifacts"] });
}

export function useEntityDetail(entityId: string | null) {
  return useQuery({
    queryKey: ["entity", entityId] as const,
    enabled: entityId !== null,
    retry: false,
    queryFn: async (): Promise<EntityDetail> => {
      const { data, error, response } = await api.GET("/api/v1/entities/{entity_id}", {
        params: { path: { entity_id: entityId as string } },
      });
      if (error || !data) throw refusalFrom(response, error, "This company could not be read.");
      return data;
    },
  });
}

export function useAmendEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { entityId: string } & EntityAmend): Promise<EntityAmended> => {
      const { entityId, ...body } = input;
      const { data, error, response } = await api.PATCH("/api/v1/entities/{entity_id}", {
        params: { path: { entity_id: entityId } },
        body,
      });
      if (error || !data) throw provisioningRefusal(response, error);
      return data;
    },
    onSuccess: () => {
      invalidateLabels(queryClient);
      // A company's jurisdiction, year end and parent status decide which filings exist, and the
      // obligations list is what shows them - unlike an ownership edit, this one is read by the
      // next determination *and* changes what a determination row is about.
      void queryClient.invalidateQueries({ queryKey: ["obligations"] });
    },
  });
}

export function useFacilityDetail(facilityId: string | null) {
  return useQuery({
    queryKey: ["facility", facilityId] as const,
    enabled: facilityId !== null,
    retry: false,
    queryFn: async (): Promise<FacilityDetail> => {
      const { data, error, response } = await api.GET("/api/v1/facilities/{facility_id}", {
        params: { path: { facility_id: facilityId as string } },
      });
      if (error || !data) throw refusalFrom(response, error, "This site could not be read.");
      return data;
    },
  });
}

export function useAmendFacility() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { facilityId: string } & FacilityAmend): Promise<FacilityAmended> => {
      const { facilityId, ...body } = input;
      const { data, error, response } = await api.PATCH("/api/v1/facilities/{facility_id}", {
        params: { path: { facility_id: facilityId } },
        body,
      });
      if (error || !data) throw provisioningRefusal(response, error);
      return data;
    },
    onSuccess: () => invalidateLabels(queryClient),
  });
}

export function useOwnership(enabled = true) {
  return useQuery({
    queryKey: ["ownership"] as const,
    enabled,
    retry: false,
    queryFn: async (): Promise<Ownership[]> => {
      const { data, error, response } = await api.GET("/api/v1/ownership", {});
      // Thrown, not defaulted: an empty array here would read as "no group has been recorded",
      // which on the screen that records one invites a duplicate of what is already there.
      if (error) throw refusalFrom(response, error, "The group's ownership could not be read.");
      return data ?? [];
    },
  });
}

/** What an ownership change makes stale: the list, and the reports whose partial-member and
 * structural-change notices are built from the graph. **Not `["obligations"]`** - see the file
 * header. */
function invalidateGroup(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["ownership"] });
  void queryClient.invalidateQueries({ queryKey: ["report"] });
  void queryClient.invalidateQueries({ queryKey: ["entities"] });
}

export function useRecordOwnership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: OwnershipIn): Promise<OwnershipRecorded> => {
      const { data, error, response } = await api.POST("/api/v1/ownership", { body: input });
      if (error || !data) throw provisioningRefusal(response, error);
      return data;
    },
    onSuccess: () => invalidateGroup(queryClient),
  });
}

export function useAmendOwnership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: { ownershipId: string } & OwnershipAmend,
    ): Promise<OwnershipAmended> => {
      const { ownershipId, ...body } = input;
      const { data, error, response } = await api.PATCH("/api/v1/ownership/{ownership_id}", {
        params: { path: { ownership_id: ownershipId } },
        body,
      });
      if (error || !data) throw provisioningRefusal(response, error);
      return data;
    },
    onSuccess: () => invalidateGroup(queryClient),
  });
}

export function useChangeMemberPeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: { userId: string } & MemberPeriodChange,
    ): Promise<MemberPeriod> => {
      const { userId, ...body } = input;
      const { data, error, response } = await api.PATCH("/api/v1/members/{user_id}/period", {
        params: { path: { user_id: userId } },
        body,
      });
      // The server's sentence **and its remedy**: `engagement_end_in_the_past` says to suspend
      // instead, which is the one thing that makes the refusal useful.
      if (error || !data) throw refusalFrom(response, error, "That period could not be changed.");
      return data;
    },
    // In force on the person's next request, so the roster is the only read that is stale.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["members"] }),
  });
}
